import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { Reveal } from "./Reveal";

const IMAGES = [
  {
    id: 1,
    category: "patients",
    src: "/assets/app-today.webp",
    alt: "DENSA Today screen with daily appointments, collections and urgent stock alerts",
    caption: "Today — your clinic at a glance",
  },
  {
    id: 2,
    category: "patients",
    src: "/assets/app-patients.webp",
    alt: "DENSA Patients screen with searchable patient list and quick-call actions",
    caption: "Every patient, organised",
  },
  {
    id: 3,
    category: "patients",
    src: "/assets/app-appointments.webp",
    alt: "DENSA Appointments calendar in month view with doctor filters",
    caption: "Appointments, clearly planned",
  },
  {
    id: 4,
    category: "operations",
    src: "/assets/app-inventory.webp",
    alt: "DENSA Inventory screen showing stock value, low-stock and expiring items",
    caption: "Inventory under control",
  },
  {
    id: 5,
    category: "operations",
    src: "/assets/app-labwork.webp",
    alt: "DENSA Lab Work screen tracking pending, urgent and delayed lab cases",
    caption: "Lab work, never forgotten",
  },
];

const TABS = [
  { id: "all", label: "All" },
  { id: "patients", label: "Patients & Schedule" },
  { id: "operations", label: "Inventory & Lab" },
];

const Gallery = () => {
  const [tab, setTab] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const visible = tab === "all" ? IMAGES : IMAGES.filter((img) => img.category === tab);

  useEffect(() => {
    document.body.style.overflow = lightbox ? "hidden" : "";
    if (lightbox) window.__lenis?.stop();
    else window.__lenis?.start();
    return () => {
      document.body.style.overflow = "";
      window.__lenis?.start();
    };
  }, [lightbox]);

  return (
    <section id="gallery" data-testid="gallery-section" className="bg-white py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand">App Gallery</p>
            <h2 className="mt-4 text-2xl font-bold tracking-tight text-brand-ink sm:text-3xl lg:text-4xl">
              The real product. <span className="text-brand">Straight from the app.</span>
            </h2>
          </div>
          <div className="flex gap-2" role="tablist" aria-label="Gallery categories">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                data-testid={`gallery-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`min-h-[44px] rounded-full border px-5 py-2 text-sm font-semibold transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand ${
                  tab === t.id
                    ? "border-brand bg-brand text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:border-brand/40 hover:text-brand"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {visible.map((img) => (
              <motion.button
                layout
                key={img.id}
                type="button"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`gallery-item-${img.id}`}
                onClick={() => setLightbox(img)}
                aria-label={`Open image: ${img.alt}`}
                className="group relative overflow-hidden rounded-2xl text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="aspect-[3/4] w-full bg-white object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-brand-deep/70 to-transparent px-5 pb-4 pt-10 text-sm font-semibold text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  {img.caption}
                </span>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-brand-deep/90 p-4 backdrop-blur-sm"
            onClick={() => setLightbox(null)}
            role="dialog"
            aria-modal="true"
            aria-label={lightbox.alt}
            data-testid="gallery-lightbox-modal"
          >
            <button
              type="button"
              data-testid="gallery-lightbox-close"
              onClick={() => setLightbox(null)}
              aria-label="Close image viewer"
              className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <X className="h-5 w-5" />
            </button>
            <motion.figure
              initial={{ scale: 0.92, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-4xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={lightbox.src} alt={lightbox.alt} className="max-h-[78vh] w-full rounded-2xl object-contain" />
              <figcaption className="mt-3 text-center text-sm text-emerald-50/80">{lightbox.caption}</figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
